import React from "react";
import {
  Box,
  Button,
  Container,
  Heading,
  VStack,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  HStack,
  Badge,
  Text,
} from "@chakra-ui/react";
import { FiEdit2, FiTrash2, FiPlus } from "react-icons/fi";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getComponent, brandConfig } from "../../brandConfig";

const GET_SESSION_TYPES = gql`
  query GetSessionTypes {
    sessionTypes {
      id
      name
      description
      format
      duration
      price
      color
      availableOnCalendar
      clientId
      meetingLink
    }
  }
`;


const DELETE_SESSION_TYPE = gql`
  mutation DeleteSessionType($id: ID!) {
    deleteSessionType(id: $id)
  }
`;

interface SessionTypeFormData {
  id?: string;
  name: string;
  description: string;
  format: "INDIVIDUAL" | "GROUP";
  duration: number;
  price: number;
  color: string;
  availableOnCalendar: boolean;
  clientId: string;
  meetingLink?: string;
}

export const SessionTypesPage = () => {
  const { isAuthenticated } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { data } = useQuery(GET_SESSION_TYPES);
  const [deleteSessionType] = useMutation(DELETE_SESSION_TYPE);

  // Brand styling variables
  const bgMain = getColor("background.main");
  const cardGradientBg = getColor("background.cardGradient");
  const cardBorder = getColor("border.darkCard");
  const textPrimary = getColor("text.primaryDark");
  const textSecondary = getColor("text.secondaryDark");
  const textMuted = getColor("text.mutedDark");
  const primaryColor = getColor("primary");
  const primaryHover = getColor("primaryHover");
  const successGreen = getColor("successGreen");
  const errorRed = getColor("status.error");
  const infoBlue = getColor("status.info");
  const warningColor = getColor("status.warning");
  const purpleAccent = getColor("purpleAccent");

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this session type?")) {
      try {
        await deleteSessionType({
          variables: { id },
          refetchQueries: ["GetSessionTypes"],
        });
        toast({
          title: "Session type deleted",
          status: "success",
          duration: 3000,
        });
      } catch (error) {
        toast({
          title: "Error deleting session type",
          status: "error",
          duration: 5000,
        });
      }
    }
  };

  if (!isAuthenticated) {
    return (
      <Box bg={bgMain} minH="100vh" display="flex" flexDirection="column">
        <NavbarWithCallToAction />
        <Container maxW="container.xl" py={8} flex="1">
          <Text color={textPrimary} fontFamily={brandConfig.fonts.body}>
            Please log in to access this page.
          </Text>
        </Container>
        <Box position="sticky" bottom={0} zIndex={10}>
          <FooterWithFourColumns />
        </Box>
      </Box>
    );
  }

  return (
    <Box bg={bgMain} minH="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />
      <Container maxW="container.xl" py={8} flex="1">
        <VStack spacing={8} align="stretch">
          <HStack justify="space-between">
            <Heading size="lg" color={textPrimary} fontFamily={brandConfig.fonts.heading}>
              Session Types
            </Heading>
            <Button
              leftIcon={<FiPlus />}
              bg={primaryColor}
              color="white"
              onClick={() => navigate("/sessions/types/create")}
              _hover={{
                bg: primaryHover,
                transform: "translateY(-2px)"
              }}
              _active={{ transform: "translateY(0)" }}
              transition="all 0.2s"
              fontWeight="semibold"
              fontFamily={brandConfig.fonts.body}
            >
              New Session Type
            </Button>
          </HStack>

          <Box
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            border="1px"
            borderColor={cardBorder}
            borderRadius="lg"
            overflow="hidden"
          >
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th color={textMuted} borderColor={cardBorder} fontFamily={brandConfig.fonts.heading}>Name</Th>
                  <Th color={textMuted} borderColor={cardBorder} fontFamily={brandConfig.fonts.heading}>Format</Th>
                  <Th color={textMuted} borderColor={cardBorder} fontFamily={brandConfig.fonts.heading}>Duration</Th>
                  <Th color={textMuted} borderColor={cardBorder} fontFamily={brandConfig.fonts.heading}>Price</Th>
                  <Th color={textMuted} borderColor={cardBorder} fontFamily={brandConfig.fonts.heading}>Calendar</Th>
                  <Th color={textMuted} borderColor={cardBorder} fontFamily={brandConfig.fonts.heading}>Client ID</Th>
                  <Th color={textMuted} borderColor={cardBorder} fontFamily={brandConfig.fonts.heading}>Meeting Link</Th>
                  <Th color={textMuted} borderColor={cardBorder} fontFamily={brandConfig.fonts.heading}>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data?.sessionTypes.map((type: SessionTypeFormData) => (
                  <Tr
                    key={type.id}
                    _hover={{ bg: "rgba(255, 255, 255, 0.05)" }}
                    transition="all 0.2s"
                  >
                    <Td borderColor={cardBorder}>
                      <HStack spacing={3} align="center">
                        {type.color && (
                          <Box
                            w="3"
                            h="3"
                            borderRadius="full"
                            bg={type.color}
                            flexShrink={0}
                          />
                        )}
                        <Box>
                          <Text fontWeight="medium" color={textPrimary} fontFamily={brandConfig.fonts.heading}>
                            {type.name}
                          </Text>
                        </Box>
                      </HStack>
                    </Td>
                    <Td borderColor={cardBorder}>
                      <Badge
                        bg={type.format === "INDIVIDUAL" ? "rgba(34, 197, 94, 0.2)" : "rgba(59, 130, 246, 0.2)"}
                        color={type.format === "INDIVIDUAL" ? successGreen : infoBlue}
                        border="1px solid"
                        borderColor={type.format === "INDIVIDUAL" ? "rgba(34, 197, 94, 0.3)" : "rgba(59, 130, 246, 0.3)"}
                      >
                        {type.format}
                      </Badge>
                    </Td>
                    <Td borderColor={cardBorder}>
                      <Text color={textSecondary} fontFamily={brandConfig.fonts.body}>
                        {type.duration} min
                      </Text>
                    </Td>
                    <Td borderColor={cardBorder}>
                      <Text color={textSecondary} fontFamily={brandConfig.fonts.body}>
                        ${type.price}
                      </Text>
                    </Td>
                    <Td borderColor={cardBorder}>
                      <Badge
                        bg={type.availableOnCalendar ? "rgba(34, 197, 94, 0.2)" : "rgba(107, 114, 128, 0.2)"}
                        color={type.availableOnCalendar ? successGreen : textMuted}
                        border="1px solid"
                        borderColor={type.availableOnCalendar ? "rgba(34, 197, 94, 0.3)" : "rgba(107, 114, 128, 0.3)"}
                      >
                        {type.availableOnCalendar ? "Available" : "Hidden"}
                      </Badge>
                    </Td>
                    <Td borderColor={cardBorder}>
                      <Text
                        fontSize="xs"
                        color={textMuted}
                        fontFamily={brandConfig.fonts.body}
                        isTruncated
                        maxW="100px"
                      >
                        {type.clientId}
                      </Text>
                    </Td>
                    <Td borderColor={cardBorder}>
                      <Badge 
                        bg={type.meetingLink ? "rgba(59, 130, 246, 0.2)" : "rgba(107, 114, 128, 0.2)"}
                        color={type.meetingLink ? infoBlue : textMuted}
                        border="1px solid"
                        borderColor={type.meetingLink ? "rgba(59, 130, 246, 0.3)" : "rgba(107, 114, 128, 0.3)"}
                      >
                        {type.meetingLink ? "Yes" : "No"}
                      </Badge>
                    </Td>
                    <Td borderColor={cardBorder}>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<FiEdit2 />}
                          aria-label="Edit"
                          size="sm"
                          bg="rgba(255, 255, 255, 0.1)"
                          color={textPrimary}
                          _hover={{ bg: "rgba(255, 255, 255, 0.2)" }}
                          onClick={() => navigate(`/sessions/edit/${type.id}`)}
                        />
                        <IconButton
                          icon={<FiTrash2 />}
                          aria-label="Delete"
                          size="sm"
                          bg={errorRed}
                          color="white"
                          _hover={{ bg: errorRed, opacity: 0.8 }}
                          onClick={() => handleDelete(type.id!)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </VStack>
      </Container>
      <Box position="sticky" bottom={0} zIndex={10}>
        <FooterWithFourColumns />
      </Box>
    </Box>
  );
};
